import { useState } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { extractErrorMessage, useAddFollower, useLeaveTicket, useRemoveFollower } from '@/queries/ticketMutations';
import { useColleagueSearch, useUserSearch } from '@/queries/people';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { SearchResults } from './SearchResults';
import type { Ticket } from '@/types/ticket';

type Audience = 'staff' | 'customer';

const DESCRIPTIONS: Record<Audience, string> = {
  staff: 'People added here can see and reply to this ticket, and get its emails.',
  customer: 'Colleagues added here can see and reply to this ticket, and get its emails.'
};

// Customers besides the requester who can see and reply to the ticket.
//
// Staff rail: only rendered on the admin's own unlensed route (TicketDetailView
// gates it on the requester block), which is the detail cache slot the
// follower mutations write to.
//
// Customer rail: the reporter and company admins manage the list (the API's
// canManageFollowers), a follower can only leave, and anyone else just reads
// it. TicketDetailView never passes controls under the read-only view-as lens.
export function TicketFollowers({
  ticket,
  audience = 'staff',
  canManage = true,
  meId,
  onLeft
}: {
  ticket: Ticket;
  audience?: Audience;
  canManage?: boolean;
  meId?: string;
  onLeft?: () => void;
}) {
  const [query, setQuery] = useState('');
  const [error, setError] = useState<string | null>(null);
  const debounced = useDebouncedValue(query);
  // Only one of the two ever has a term, so only one asks the API.
  const staffSearch = useUserSearch(audience === 'staff' ? debounced : '', ticket.companyId);
  const colleagueSearch = useColleagueSearch(audience === 'customer' ? debounced : '');
  const search = audience === 'staff' ? staffSearch : colleagueSearch;
  const add = useAddFollower(ticket._id);
  const remove = useRemoveFollower(ticket._id);
  const leave = useLeaveTicket(ticket._id);

  const followers = ticket.followerUsers ?? [];
  const following = new Set([...(ticket.followers ?? []), ...followers.map(f => f._id)]);
  const canLeave = audience === 'customer' && !canManage && Boolean(meId && following.has(meId));
  // The API refuses staff, the requester and duplicates, so they are never offered.
  const candidates = search.data?.filter(u => !('isAdmin' in u && u.isAdmin) && u._id !== ticket.createdBy && !following.has(u._id));

  const run = async (action: () => Promise<unknown>, fallback: string) => {
    setError(null);
    try {
      await action();
    } catch (err) {
      setError(extractErrorMessage(err, fallback));
    }
  };

  return (
    <section data-testid="ticket-followers" aria-label="People on this ticket" className="mb-5 border-b border-etn-border-hairline pb-5">
      <h2 className="text-etn-micro text-etn-text-label">People on this ticket</h2>
      <p className="mt-0.5 text-etn-micro text-etn-text-soft">{DESCRIPTIONS[audience]}</p>

      {followers.length > 0 && (
        <ul className="mt-2.5 space-y-1.5">
          {followers.map(f => (
            <li key={f._id} className="flex items-center justify-between gap-2">
              <div className="min-w-0 text-etn-control">
                <div className="truncate text-etn-text-cell">
                  {f.name || f.email || f._id}
                  {f._id === meId && <span className="ml-1.5 text-etn-micro text-etn-text-soft">(you)</span>}
                </div>
                {f.name && f.email && <div className="truncate text-etn-micro text-etn-text-soft">{f.email}</div>}
              </div>
              {canManage && (
                <Button
                  variant="ghost"
                  size="icon-xs"
                  aria-label={`Remove ${f.name || f.email || 'person'}`}
                  disabled={remove.isPending}
                  onClick={() => run(() => remove.mutateAsync(f._id), 'Could not remove that person')}
                >
                  <X />
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}

      {canManage && (
        <div className="mt-2.5">
          <Input
            aria-label="Add person"
            placeholder={audience === 'customer' ? 'Add a colleague' : 'Add person'}
            autoComplete="off"
            value={query}
            disabled={add.isPending}
            onChange={e => setQuery(e.target.value)}
          />
          <SearchResults
            label="People to add"
            term={query}
            items={candidates}
            isFetching={search.isFetching || query !== debounced}
            onPick={user =>
              run(async () => {
                await add.mutateAsync(user._id);
                setQuery('');
              }, 'Could not add that person')
            }
            renderItem={user => (
              <>
                <div className="truncate text-etn-text-cell">{user.name || user.email || user._id}</div>
                {user.name && user.email && <div className="truncate text-etn-micro text-etn-text-soft">{user.email}</div>}
              </>
            )}
          />
        </div>
      )}

      {canLeave && meId && (
        <Button
          variant="outline"
          size="xs"
          className="mt-2.5"
          disabled={leave.isPending}
          onClick={() =>
            run(async () => {
              const result = await leave.mutateAsync(meId);
              if (!('ticket' in result)) onLeft?.();
            }, 'Could not take you off this ticket')
          }
        >
          Leave ticket
        </Button>
      )}

      {error && (
        <p role="alert" className="mt-1.5 text-etn-micro text-etn-danger-text">
          {error}
        </p>
      )}
    </section>
  );
}
